import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import * as amplitude from '@amplitude/analytics-browser';

import { SkeletonBase } from '@unified-checkout/design-system';

import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { getMainData } from '@app/dashboard/services/inner_layout/bills/get-main-data';
import { confirmPayment } from '@app/dashboard/services/inner_layout/bills/confirm-payment';
import { confirmSmsSteps } from '@app/dashboard/services/inner_layout/bills/confirm-sms-steps';

import {
  BILLS_EVENTS,
  BILLS_EVENTS_PARAMS
} from '@app/dashboard/constants/tracking_events/bills';

import type { ICategory } from '@app/dashboard/types/innerLayout/bills/categories';

import { AdsCard } from './AdsCard';
import { CategoryCard } from './CategoryCard';
import { CategoryModal } from './CategoryModal';
import type { TCategoryModalResult } from '@app/dashboard/types/innerLayout/bills/category-modal';

import styles from './all-services.module.scss';

interface IAllServicesProps {
  billsData?: any;
  isPending?: boolean;
}

export const AllServices = ({ billsData, isPending = false }: IAllServicesProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState<ICategory | null>(null);
  const [result, setResult] = useState<TCategoryModalResult>(null);

  const categories = (billsData?.data?.data?.categories ?? []).filter(
    (category: any) => category.redirection_screen === 'BILLS'
  ) as ICategory[];

  const showAdsCard = (billsData?.data?.data?.categories ?? []).some(
    (category: any) => category.redirection_screen === 'CONVERTEDPAY'
  );

  const { mutate: handleConfirmPayment, isPending: isSubmitting } = useMutation({
    mutationFn: confirmPayment,
    onSuccess: (data: any) => {
      if (data?.data?.instructions) {
        setResult({
          status: 'sms',
          title: t('innerLayout.billsPage.addFundsModal.smsStepsTitle'),
          instructions: data.data.instructions,
          statusCheck: data.data.status_check,
        });
        return;
      }

      setResult({
        status: 'success',
        title: data?.data?.title || t('innerLayout.billsPage.allServices.paymentSuccess'),
        description: data?.data?.description || t('innerLayout.billsPage.allServices.paymentSuccessHint'),
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MAIN_DATA] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_SAVED_BILLS] });
    },
    onError: (error: any) => {
      const errData = error?.response?.data;
      setResult({
        status: 'error',
        title: errData?.title || t('errors.somethingWentWrong'),
        description: errData?.description,
      });
    },
  });

  const { mutate: handleConfirmSms, isPending: isConfirmingSms } = useMutation({
    mutationFn: confirmSmsSteps,
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MAIN_DATA] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_SAVED_BILLS] });
      setResult({
        status: 'success',
        title: data?.data?.title || t('innerLayout.billsPage.allServices.paymentSuccess'),
        description: data?.data?.description || t('innerLayout.billsPage.allServices.paymentSuccessHint'),
      });
    },
    onError: (error: any) => {
      const errData = error?.response?.data;
      setResult({
        status: 'error',
        title: errData?.message || t('errors.somethingWentWrong'),
        description: errData?.description,
      });
    },
  });

  const handleSubmit = (payload: Record<string, any>, serviceId: number) => {
    if (payload._error) {
      setResult({ status: 'error', title: payload.title, description: payload.description });
      return;
    }
    handleConfirmPayment({
      category_id: Number(selectedCategory?.category_id),
      service_id: serviceId,
      required_data: { ...payload },
      fees: payload.fees ?? 0,
      save_bill: payload.save_bill ?? false,
      bill_name: payload.bill_name ?? '',
      amount: payload.amount,
      bill_id: payload.bill_id ?? [],
    });
  };

  const handleClose = () => {
    setSelectedCategory(null);
    setResult(null);
  };

  const handleCategoryClick = (category: ICategory) => {
    setSelectedCategory(category);
    amplitude.track(BILLS_EVENTS.BILLS_SERVICE_CATEGORY_SELECTED, {
      [BILLS_EVENTS_PARAMS.BILL_CATEGORY]: category.category_title,
    });
  };

  return (
    <>
      {isPending ?
        <SkeletonBase width='100%' height='21.25rem' />
        :
        showAdsCard && <AdsCard />
      }
      <div className={styles['all-services']}>
        {isPending
          ? Array.from({ length: 4 }).map((_, index) => (
            <SkeletonBase key={index} width='100%' height='8rem' />
          ))
          : categories.map((category) => (
            <CategoryCard
              key={category.category_id}
              category={category}
              onClickHandler={handleCategoryClick}
            />
          ))}
      </div>

      <CategoryModal
        category={selectedCategory}
        billsData={billsData}
        result={result}
        onClose={handleClose}
        onSubmit={handleSubmit}
        onConfirmSms={handleConfirmSms}
        isSubmitting={isSubmitting}
        isConfirmingSms={isConfirmingSms}
      />
    </>
  );
};
